import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";

type Ctx = {
  variables: string[];
  list: () => string[];
  add: (name: string) => void;                   // adiciona apenas se não existir
  upsert: (name: string) => void;                // cria (não existe update já que não há value)
  remove: (name: string) => void;
  reset: (vars?: string[]) => void;              // substitui tudo
  setMany: (vars: string[], mode?: "merge" | "replace") => void;
};

const ConexaBotVariablesContext = createContext<Ctx | null>(null);

type ProviderProps = {
  initial?: string[];       // variáveis iniciais vindas do seu "manager"
  syncFromProps?: boolean;  // se true, mantém sincronizado quando initial mudar
  children: ReactNode;
};

export function ConexaBotVariablesProvider({
  initial = [],
  syncFromProps = false,
  children,
}: ProviderProps) {
  const [variables, setVariables] = useState<string[]>(initial);

  useEffect(() => {
    if (syncFromProps) setVariables(initial);
  }, [initial, syncFromProps]);

  const list = useCallback(() => variables, [variables]);

  const add = useCallback((name: string) => {
    setVariables(prev => (prev.includes(name) ? prev : [...prev, name]));
  }, []);

  const upsert = useCallback((name: string) => {
    setVariables(prev => (prev.includes(name) ? prev : [...prev, name]));
  }, []);

  const remove = useCallback((name: string) => {
    setVariables(prev => prev.filter(v => v !== name));
  }, []);

  const reset = useCallback((vars: string[] = []) => setVariables(vars), []);

  const setMany = useCallback(
    (vars: string[], mode: "merge" | "replace" = "merge") => {
      setVariables(prev => {
        if (mode === "replace") return [...vars];
        const set = new Set([...prev, ...vars]);
        return Array.from(set);
      });
    },
    []
  );

  const value = useMemo(
    () => ({ variables, list, add, upsert, remove, reset, setMany }),
    [variables, list, add, upsert, remove, reset, setMany]
  );

  return (
    <ConexaBotVariablesContext.Provider value={value}>
      {children}
    </ConexaBotVariablesContext.Provider>
  );
}

export function useConexaBotVariables() {
  const ctx = useContext(ConexaBotVariablesContext);
  if (!ctx) {
    throw new Error(
      "useConexaBotVariables deve ser usado dentro de <ConexaBotVariablesProvider />"
    );
  }
  return ctx;
}
